import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import type {
  NotificationSettingsRequest,
  UserBulkDeleteRequest,
  UserCreateRequest,
  UserUpdateRequest,
} from "@/api/types";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { QUERY_KEYS } from "@/query-keys";
import { useTranslation } from "@/translations";

const useAddUser = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: (payload: UserCreateRequest) => api.postUsersV1(payload).then((x) => x.data),
    onSuccess: () => {
      showFlashToast({ variant: "success", message: t("page.create-user.new.success") });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.USERS_QUERY(projectId) });
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("page.create-user.new.error") });
    },
  });
};

const useUploadUser = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (payload: UserCreateRequest) => api.postUsersV1(payload),
    onError: () => {
      showFlashToast({ variant: "alert", message: t("page.user-bulk-upload.error") });
    },
  });
};

const useUpdateUser = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();
  const sessionUser = useSessionUser();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: UserUpdateRequest }) =>
      api.putUsersV1(userId, data).then((x) => x.data),
    onSuccess: async (_, payload) => {
      showFlashToast({ variant: "success", message: t("page.create-user.edit.success") });

      if (sessionUser.id === payload.userId) {
        // Editing yourself can change language, name and permissions everywhere; refresh before navigating back.
        await queryClient.invalidateQueries();
      } else {
        void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.USER_DETAILS(projectId, payload.userId) });
        void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.USERS_QUERY(projectId) });
      }
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("page.create-user.edit.error") });
    },
  });
};

const useDeleteUser = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (payload: { userId: string; projectIds: UserBulkDeleteRequest["projectIds"] }) =>
      api.postUsersDeleteV2(payload.userId, {
        projectIds: payload.projectIds,
      }),
    onSuccess: (_, payload) => {
      showFlashToast({ variant: "success", message: t("page.user-detail.delete.success") });

      queryClient.removeQueries({ queryKey: QUERY_KEYS.USER_DETAILS(projectId, payload.userId) });
      queryClient.removeQueries({ queryKey: QUERY_KEYS.USER_POSTS(projectId, payload.userId) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.USERS_QUERY(projectId) });
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("page.user-detail.delete.error") });
    },
  });
};

const useAddUserSignUpReminders = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (userId: string) => api.postUsersUserSignupRemindersV1(userId),
    onSuccess: (_, userId) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.USER_SIGNUP_REMINDERS(projectId, userId) });
      showFlashToast({ variant: "success", message: t("page.user-detail.invite-user.success") });
    },
    onError() {
      showFlashToast({ variant: "alert", message: t("page.user-detail.invite-user.error") });
    },
  });
};

const useCheckEmailExists = () => {
  const api = useApi();

  return useMutation({
    mutationFn: (email: string) => api.postUsersEmailExistsV1({ email }).then((x) => x.data),
  });
};

const useCreateHousemateInvite = () => {
  const api = useApi();

  return useMutation({
    mutationFn: () => api.postUsersResidentInvitesHousemateV1().then((response) => response.data),
  });
};

const useMarkCodeOfConductAsAgreed = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: () => api.postSelfCodeOfConductAgreeV1(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SELF(projectId) });
    },
    onError() {
      showFlashToast({ variant: "alert", message: t("page.onboarding.code-of-conduct.agree.notification.failed") });
    },
  });
};

const useUpdateNotificationSettings = () => {
  const projectId = useProjectId();
  const queryClient = useQueryClient();
  const api = useApi();
  const sessionUser = useSessionUser();

  return useMutation({
    mutationFn: (payload: { userId: string; data: NotificationSettingsRequest }) =>
      api.patchNotificationSettingsV1(payload.userId, payload.data),
    onSuccess: (_, payload) => {
      if (payload.userId === sessionUser.id) {
        void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SELF(projectId) });
      } else {
        void queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.NOTIFICATION_SETTINGS_USER(projectId, payload.userId),
        });
      }
    },
  });
};

const useResetIloqKey = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (userId: string) => api.postIloqKeyUsersResetV1(userId),
    onSuccess: (_, userId) => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.USER_ILOQ_STATUS(projectId, userId),
      });
      showFlashToast({ variant: "success", message: t("component.user-details-content.iloq-key-reset.success") });
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("component.user-details-content.iloq-key-reset.error") });
    },
  });
};

const useSetUserDeletionDate = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (payload: {
      userId: string;
      projectIds: UserBulkDeleteRequest["projectIds"];
      date: Date | undefined;
    }) =>
      api.putUsersDeletionDateV1(payload.userId, {
        projectIds: payload.projectIds,
        deleteAt: payload.date?.toISOString(),
      }),
    onSuccess: (_, payload) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.USERS(projectId) });
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.USER_DETAILS(projectId, payload.userId),
      });
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.USER_POSTS(projectId, payload.userId),
      });
      showFlashToast({ variant: "success", message: t("page.user-detail.set-deletion-date.success") });
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("page.user-detail.set-deletion-date.error") });
    },
  });
};

export const userMutations = {
  useAddUserSignUpReminders,
  useAddUser,
  useUploadUser,
  useUpdateUser,
  useDeleteUser,
  useSetUserDeletionDate,
  useCheckEmailExists,
  useCreateHousemateInvite,
  useMarkCodeOfConductAsAgreed,
  useUpdateNotificationSettings,
  useResetIloqKey,
};
